import { Calendar, Activity, ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export default function HealthDataAccordion({ healthData }) {
  if (!healthData || healthData.length === 0) return null;

  // Group health data by weeks
  const sortedData = [...healthData].sort((a, b) => new Date(b.date) - new Date(a.date));
  const weeks = [];
  let currentWeek = [];
  let weekStartDate = null;

  sortedData.forEach((day, index) => {
    const dayDate = new Date(day.date);
    
    if (!weekStartDate) {
      weekStartDate = dayDate;
    }
    
    currentWeek.push(day);
    
    // Start new week every 7 days or at the end
    if (currentWeek.length === 7 || index === sortedData.length - 1) {
      weeks.push({
        startDate: weekStartDate,
        endDate: dayDate,
        days: [...currentWeek]
      });
      currentWeek = [];
      weekStartDate = null;
    }
  });

  return (
    <div className="mt-8 mb-6" data-testid="health-data-accordion">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
        <Calendar className="w-6 h-6 mr-2 text-blue-600" />
        Health Data by Week
      </h2>
      <Accordion type="single" collapsible className="space-y-4">
        {weeks.map((week, weekIndex) => {
          const weekNum = weeks.length - weekIndex;
          const avgHeartRate = Math.round(week.days.reduce((sum, d) => sum + (d.heart_rate_avg || 0), 0) / week.days.length);
          const avgSteps = Math.round(week.days.reduce((sum, d) => sum + (d.steps || 0), 0) / week.days.length);
          const totalActiveCal = Math.round(week.days.reduce((sum, d) => sum + (d.active_calories || 0), 0));
          const totalExercise = Math.round(week.days.reduce((sum, d) => sum + (d.exercise_minutes || 0), 0));
          
          return (
            <AccordionItem 
              key={weekIndex} 
              value={`week-${weekIndex}`}
              className="bg-white rounded-xl shadow-lg overflow-hidden border border-blue-100"
            >
              <AccordionTrigger className="hover:no-underline">
                <div className="w-full">
                  {/* Week Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-left">
                          Week {weekNum}
                        </h3>
                        <p className="text-blue-100 text-sm mt-1 text-left">
                          {week.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {week.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <ChevronDown className="w-6 h-6 transition-transform duration-200" />
                    </div>
                  </div>

                  {/* Week Summary - Always Visible */}
                  <div className="bg-blue-50 px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">Avg Heart Rate</p>
                      <p className="text-lg font-bold text-red-600">
                        {avgHeartRate} bpm
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">Avg Steps</p>
                      <p className="text-lg font-bold text-green-600">
                        {avgSteps.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">Total Active Cal</p>
                      <p className="text-lg font-bold text-orange-600">
                        {totalActiveCal.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">Total Exercise</p>
                      <p className="text-lg font-bold text-blue-600">
                        {totalExercise} min
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent>
                {/* Daily Cards - Collapsed by Default */}
                <div className="px-6 py-4 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Daily Breakdown
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {week.days.map((day, dayIndex) => {
                      const date = new Date(day.date);
                      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                      const dayDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      
                      return (
                        <div 
                          key={dayIndex} 
                          className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200 hover:shadow-lg transition-shadow"
                        >
                          {/* Day Header */}
                          <div className="text-center mb-3 pb-2 border-b border-gray-200">
                            <h4 className="text-sm font-bold text-gray-900">{dayName}</h4>
                            <p className="text-xs text-gray-600">{dayDate}</p>
                          </div>

                          {/* Metrics Grid */}
                          <div className="space-y-2">
                            {/* Heart Rate */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <Activity className="w-3 h-3 text-red-500" />
                                <span className="text-xs text-gray-600">Heart</span>
                              </div>
                              <span className="text-xs font-semibold text-red-600">
                                {day.heart_rate_avg || '-'} bpm
                              </span>
                            </div>

                            {/* Steps */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Steps</span>
                              <span className="text-xs font-semibold text-green-600">
                                {day.steps?.toLocaleString() || '-'}
                              </span>
                            </div>

                            {/* Calories */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Calories</span>
                              <span className="text-xs font-semibold text-orange-600">
                                {day.active_calories || '-'} cal
                              </span>
                            </div>

                            {/* Exercise */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Exercise</span>
                              <span className="text-xs font-semibold text-blue-600">
                                {day.exercise_minutes || '-'} min
                              </span>
                            </div>

                            {/* Sleep */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Sleep</span>
                              <span className="text-xs font-semibold text-purple-600">
                                {day.sleep_hours?.toFixed(1) || '-'} hrs
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
